// User service following SRP, SOC, and DRY principles
// Uses UserApiClient for all user-related operations

import { UserApiClient } from "@/lib/api/clients/user-api-client";
import { AppBaseService as BaseService } from "./base/app-base-service";
import { ServiceResult } from "./base/service-types";
import {
  User,
  UpdateUserRequest,
  UserPublic,
  Organization,
} from "@/lib/types/api/user-types";

/**
 * User service for handling user profile and account operations
 * Follows SRP: Single responsibility for user operations
 * Follows SOC: Separates user business logic from HTTP concerns
 * Follows DRY: Uses enhanced base service patterns
 */
export class UserService extends BaseService {
  readonly serviceName = "UserService";

  constructor(private userApiClient: UserApiClient = new UserApiClient()) {
    super();
  }

  /**
   * Get current user profile
   * @returns Promise<ServiceResult<User>>
   */
  async getCurrentUser(): Promise<ServiceResult<User>> {
    return this.executeWithErrorHandling(() => this.userApiClient.getMe(), {
      operation: "getCurrentUser",
      entity: "user",
    });
  }

  /**
   * Update user profile with validation
   * @param data - User update data
   * @returns Promise<ServiceResult<User>>
   */
  async updateProfile(data: UpdateUserRequest): Promise<ServiceResult<User>> {
    // Validate required fields
    this.validateRequired(data as Record<string, unknown>, ["full_name"], {
      operation: "updateProfile",
      entity: "user",
    });

    // Validate password fields if provided
    if (data.password && data.current_password && data.password.length < 8) {
      return this.createErrorResult({
        message: "Password must be at least 8 characters long",
        code: "VALIDATION_ERROR",
        validationErrors: [
          {
            field: "password",
            message: "Password must be at least 8 characters long",
            code: "MIN_LENGTH",
          },
        ],
      });
    }

    return this.executeWithErrorHandling(
      () => this.userApiClient.updateMe(data),
      {
        operation: "updateProfile",
        entity: "user",
        metadata: { fieldsUpdated: Object.keys(data) },
      }
    );
  }

  /**
   * Delete user account
   * @returns Promise<ServiceResult<void>>
   */
  async deleteAccount(): Promise<ServiceResult<{ message: string }>> {
    return this.executeWithErrorHandling(
      () => this.userApiClient.deleteAccount(),
      {
        operation: "deleteAccount",
        entity: "user",
      }
    );
  }

  /**
   * Search users by query
   * @param query - Search query string
   * @returns Promise<ServiceResult<UserPublic[]>>
   */
  async searchUsers(query: string): Promise<ServiceResult<UserPublic[]>> {
    // Validate query
    if (!query || query.trim().length < 2) {
      return this.createErrorResult({
        message: "Search query must be at least 2 characters long",
        code: "VALIDATION_ERROR",
        validationErrors: [
          {
            field: "query",
            message: "Query must be at least 2 characters long",
            code: "MIN_LENGTH",
          },
        ],
      });
    }

    return this.executeWithErrorHandling(
      () => this.userApiClient.searchUsers(query.trim()),
      {
        operation: "searchUsers",
        entity: "user",
        metadata: { query: query.trim() },
      }
    );
  }

  /**
   * Get the public organization
   * Used to determine if a user is in the public organization
   * @returns Promise<ServiceResult<Organization>>
   */
  async getPublicOrganization(): Promise<ServiceResult<Organization>> {
    return this.executeWithErrorHandling(
      () => this.userApiClient.getPublicOrganization(),
      {
        operation: "getPublicOrganization",
        entity: "organization",
      }
    );
  }

  /**
   * Check if user profile is complete
   * @param user - User object to check
   * @returns boolean indicating if profile is complete
   */
  isProfileComplete(user: User): boolean {
    const requiredFields = ["full_name", "email"];
    return requiredFields.every(field => {
      const value = user[field as keyof User];
      return value !== null && value !== undefined && value !== "";
    });
  }

  /**
   * Get missing profile fields
   * @param user - User object to check
   * @returns Array of missing field names
   */
  getMissingProfileFields(user: User): string[] {
    const requiredFields = ["full_name", "email"];
    return requiredFields.filter(field => {
      const value = user[field as keyof User];
      return value === null || value === undefined || value === "";
    });
  }

  /**
   * Validate email format
   * @param email - Email string to validate
   * @returns boolean indicating if email is valid
   */

  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

/**
 * Default singleton instance for ease of use
 * Can be overridden with dependency injection in tests
 */
export const userService = new UserService();
